/* eslint-disable no-console */
import Player from "../classes/Player";
import _ from 'lodash';
import StartOverlay from "../classes/StartOverlay";

export default class MainScene extends Phaser.Scene {

    preload() {
        this.trans = this.game._vm.trans;

        this.innerWidth = this.game.config.width;
        this.innerHeight = this.game.config.height;

        this.centerX = this.innerWidth / 2;
        this.centerY = this.innerHeight / 2;

        this.is_mobile = this.game.isMobile();

        this.textures.remove('gradient');

        //Create a new texture for the gradient background
        let texture = this.textures.createCanvas('gradient', this.innerWidth, this.innerHeight);
        let context = texture.getContext();
        let grd = context.createLinearGradient(0, 0, 0, this.innerHeight);

        //Draw the gradient
        grd.addColorStop(0, '#18264a');
        grd.addColorStop(1, '#2b4895');

        //Because of WebGL, we need to refresh the texture.
        texture.refresh();

        context.fillStyle = grd;
        context.fillRect(0, 0, this.innerWidth, this.innerHeight);

        //Adding the newly drawn gradient to the scene
        let gradient = this.add.image(this.innerWidth / 2, this.innerHeight / 2, 'gradient');

        //Draw the progress bar
        let progressBar = this.add.graphics();
        let progressBox = this.add.graphics();

        progressBox.lineStyle(2, 0xCDDA00, 1);
        progressBox.strokeRoundedRect(this.centerX - 125, this.centerY, 250, 20, 10);
        progressBox.lineStyle(4, 0xCDDA00, 1);
        progressBox.setVisible(false);
        progressBar.setVisible(false);

        //Add the loading text
        let percentText = this.make.text({
            x: Math.round(this.centerX),
            y: Math.round(this.centerY) - 30,
            resolution: 3000,
            text: this.trans('loading.getting_ready', 'Getting ready...'),
            style: {
                font: '200 18px "Scandinavian", sans-serif',
                fill: '#ffffff'
            }
        });

        //Set the loading text pointer to center
        percentText.setOrigin(0.5, 0.5);
        percentText.setVisible(false);

        //Add the information text
        let infoText = this.make.text({
            x: this.centerX,
            y: this.centerY + 75,
            text: this.trans('start.info_1', 'Turn on the sound!') + '\n' + this.trans('start.info_2', 'Use the arrow keys to track the beat') + '\n' + this.trans('start.info_3', 'and make some wild moves'),
            style: {
                font: '200 18px "Scandinavian", sans-serif',
                fill: '#ffffff',
                align: 'center'
            },
        });
        infoText.setOrigin(0.5, 0.5)
        infoText.setVisible(false);

        //Update the progress bar
        this.load.on('progress', (value) => {
            if (value > 0) {
                gradient.setVisible(true);
                percentText.setVisible(true);
                infoText.setVisible(true);
                progressBar.setVisible(true);
                progressBox.setVisible(true);
            }

            progressBar.clear();
            progressBar.fillStyle(0xCDDA00, 1);
            progressBar.fillRoundedRect(this.centerX - 125, this.centerY, 250 * (value < 0.10 ? 0.10 : value), 20, 10);
        });

        //When loading is completed, destroy the progress bar
        this.load.on('complete', () => {
            console.log('Asset loading completed');

            gradient.destroy();
            percentText.destroy();
            infoText.destroy();
            progressBar.destroy();
            progressBox.destroy();
        });

        //Load the necessary images
        if (this.is_mobile) {
            this.load.image("flare", "assets/img/game/flare_mobile.png");
            this.load.image("overlay", "assets/img/game/green_overlay_mobile.png");
        } else {
            this.load.image("flare", "assets/img/game/flare.png");
            this.load.image("overlay", "assets/img/game/green_overlay.png");
        }

        this.dpr = parseInt(this.game.config.resolution);
        if (this.dpr > 3) {
            this.dpr = 3;
        }

        let dpr = '';
        if (this.is_mobile && this.dpr > 1) {
            dpr = '@' + this.dpr + 'x';
        }

        this.load.image("left-icon", "assets/img/game/arrows/arrow-left" + dpr + ".png");
        this.load.image("right-icon", "assets/img/game/arrows/arrow-right" + dpr + ".png");
        this.load.image("wrong-left-icon", "assets/img/game/arrows/arrow-left-inc" + dpr + ".png");
        this.load.image("wrong-right-icon", "assets/img/game/arrows/arrow-right-inc" + dpr + ".png");
        this.load.image("correct-right-icon", "assets/img/game/arrows/arrow-right-correct" + dpr + ".png");
        this.load.image("correct-left-icon", "assets/img/game/arrows/arrow-left-correct" + dpr + ".png");
        this.load.image("circle", "assets/img/game/disc-pressed" + dpr + ".png");
        this.load.image("bell", "assets/img/game/lives-left" + dpr + ".png");
        this.load.image("bell-grey", "assets/img/game/lost-life" + dpr + ".png");
        this.load.image("refresh", "assets/img/game/refresh" + dpr + ".png");
        this.load.image("rotate", "assets/img/game/rotate" + dpr + ".png");

        this.load.image("keyboard", "assets/img/game/keyboard" + dpr + ".png");

        this.load.svg("left-key", "assets/img/game/arrows/arrow-left@1x.svg", {
            scale: 1.4 * this.dpr
        });
        this.load.svg("right-key", "assets/img/game/arrows/arrow-right@1x.svg", {
            scale: 1.4 * this.dpr
        });

        //Load the background sequence
        if (this.is_mobile) {
            for (let i = 0; i < 35; i++) {
                this.load.image('bg' + i, 'assets/img/game/mobile_bg/' + i + '.jpg');
            }
        } else {
            for (let i = 0; i < 36; i++) {
                this.load.image('bg' + i, 'assets/img/game/bg/' + i + '.jpg');
            }
        }

        //Load player animations
        this.load.multiatlas('player', 'assets/img/game/player_atlas/sprite.json', 'assets/img/game/player_atlas/');

        //Load the sounds
        this.load.audio('intro', [
            'assets/sounds/intro.ogg',
            'assets/sounds/intro.mp3'
        ]);
        this.load.audio('master', [
            'assets/sounds/master.ogg',
            'assets/sounds/master.mp3'
        ]);
        this.load.audio('rewind', [
            'assets/sounds/rewind.ogg',
            'assets/sounds/rewind.mp3'
        ]);
        this.load.audio('game-over', [
            'assets/sounds/game-over.ogg',
            'assets/sounds/game-over.mp3'
        ]);
        this.load.audio('smash', [
            'assets/sounds/smash.ogg',
            'assets/sounds/smash.mp3'
        ]);
        this.load.audio('hit', [
            'assets/sounds/hit.ogg',
            'assets/sounds/hit.mp3'
        ]);

        //Load the arrows from the JSON file
        this.loaded_arrows = require('../json/arrows.json');

        //If the game is in playback mode, load previously recorded arrows
        if (this.game.playback_mode) {
            let arrows = localStorage.getItem('recordedArrows');
            if (arrows) {
                this.loaded_arrows = JSON.parse(arrows);
            }
        }

        this.speed_markers = require('../json/speed.json');
    }

    create() {
        console.log(this);

        let isMobile = this.game.isMobile();

        //If the device originally was a phone, make the user refresh the page
        if (this.game.was_mobile !== isMobile) {
            //Add the information text
            let infoText = this.make.text({
                x: this.centerX,
                y: this.centerY - 75,
                text: this.trans('start.refresh', "REFRESH THE PAGE TO START THE GAME"),
                style: {
                    font: '200 18px "Scandinavian", sans-serif',
                    fill: '#ffffff',
                    align: 'center'
                },
            });
            infoText.setOrigin(0.5, 0.5)

            let refreshButton = this.add.sprite(this.centerX, this.centerY, 'refresh');
            refreshButton.setInteractive();

            if (this.dpr > 1) {
                refreshButton.setScale(refreshButton.scaleX / this.dpr)
            }

            //Change the cursor to pointer, when hovering over start button
            refreshButton.on('pointerover', () => {
                this.sys.canvas.style.cursor = 'pointer';
            });

            //Change the cursor back to normal, when not hovering over start button
            refreshButton.on('pointerout', () => {
                this.sys.canvas.style.cursor = 'inherit';
            });

            //Reload the page on button press
            refreshButton.on('pointerup', () => {
                window.location.reload();
            });

            return true;
        }

        //Revert the cursor back, just in case
        this.sys.canvas.style.cursor = 'inherit';

        //If the device is a phone and the screen orientation is in landscape, ask the user to flip the phone
        if (!this.game.device.os.desktop && (window.innerWidth > window.innerHeight)) {
            let rotate = this.add.sprite(this.centerX, this.centerY, 'rotate');
            if (this.dpr > 1) {
                rotate.setScale(rotate.scaleX / this.dpr)
            }

            //Add the information text
            let infoText = this.make.text({
                x: this.centerX,
                y: this.centerY + 75,
                text: this.trans('start.rotate_phone', "Rotate your phone to start the game"),
                style: {
                    font: '200 18px "Scandinavian", sans-serif',
                    fill: '#ffffff',
                    align: 'center'
                },
            });
            infoText.setOrigin(0.5, 0.5)
            return true;
        }

        this.started = false;
        this.is_ending = false;

        this.start_time = Date.now();
        this.current_second = 0;

        //Frames per second, used in animations
        this.fps = 30;

        //Clone the arrows from the loaded JSON file
        this.available_arrows = _.clone(this.loaded_arrows);

        //Remove the arrow taken from the arrow file, and generate new ones
        if (this.game.generate_arrows) {
            this.available_arrows = [];

            let availableKeys = ['left', 'right'];

            for (let i = 0; i < 500; i++) {
                if (i < 3) {
                    continue;
                }

                this.available_arrows.push({
                    time: i.toFixed(1),
                    key: availableKeys[Math.floor(Math.random() * availableKeys.length)]
                });
            }
        }

        //How many lives the player has left
        this.lives = 4;

        //Hit related stuff
        this.hits_needed = this.available_arrows.length;
        this.hits = 0;

        //Speed of the arrows
        this.speed = 100;

        //The good statuses, that will randomly show on successful hits
        this.good_statuses = [
            this.trans('statuses.good_1', 'Excellent! You\'ve got the moves!'),
            this.trans('statuses.good_2', 'Awesome! You rock!'),
            this.trans('statuses.good_3', 'You\'re on your way!'),
            this.trans('statuses.good_4', 'Look at you shine!')
        ];

        //The bad statuses, that will show on failed hits
        this.bad_statuses = [
            this.trans('statuses.bad_1', 'Happens to the best of us!'),
            this.trans('statuses.bad_2', 'Hang in there!'),
            this.trans('statuses.bad_3', 'Don\'t give up!'),
            this.trans('statuses.bad_4', 'Oops, let\'s blame the ice!')
        ];

        //Create the background animation
        let frames = [];
        for (let i = 0; i < (this.is_mobile ? 35 : 36); i++) {
            frames.push({
                key: 'bg' + i
            });
        }

        this.anims.create({
            key: 'bg',
            frames: frames,
            frameRate: this.fps,
            repeat: -1
        });

        //Add the background sprite and play the animation
        this.bg_scale = Math.min(this.innerWidth / this.textures.getFrame('bg1').width, this.innerHeight / this.textures.getFrame('bg1').height);

        this.bg = this.add.sprite(0, 0, 'bg1');
        this.bg.setScale(this.bg_scale);

        //If the screen width is larger than the background image, scale the background so it fills the entire screen
        if (this.innerWidth > this.bg.displayWidth) {
            this.bg_scale = this.innerWidth / (this.bg.displayWidth / this.bg_scale);

            this.bg.setScale(this.bg_scale);
        }

        //Calculate the center
        let bgX = this.bg.displayWidth + (this.innerWidth - this.bg.displayWidth);
        let bgY = this.bg.displayHeight + (this.innerHeight - this.bg.displayHeight);

        this.bg.setPosition(bgX / 2, bgY / 2);

        //Create the arrow group, which will hold all the arrow sprites
        this.arrows = this.physics.add.group();

        //Create empty arrows, that will be used as templates
        for (let i = 0; i < 20; i++) {
            let arrow = this.arrows.create(0, 0);

            if (this.dpr > 1) {
                arrow.setScale(arrow.scaleX / this.dpr)
            }

            arrow.setDepth(4);

            arrow.setVisible(false);
        }

        //Create the center circle sprite
        if (this.is_mobile) {
            this.circle = this.physics.add.sprite(50, this.centerY, 'circle');
        } else {
            this.circle = this.physics.add.sprite(this.centerX, this.innerHeight - 100, 'circle');
        }
        this.circle.body.setSize(30, 30);
        if (this.dpr > 1) {
            this.circle.original_scale = this.circle.scaleX / this.dpr;
            this.circle.setScale(this.circle.original_scale - (0.1 / this.dpr))
        } else {
            this.circle.original_scale = 1;
            this.circle.setScale(0.9);
        }

        this.circle.body.onCollide = true;
        this.circle.last_collision = {
            id: null,
            key: null
        };

        //On collision set the last key as the last collision, so we could quickly grab it
        this.physics.world.on('collide', (firstObject, secondObject) => {
            if (secondObject.touched) {
                return false;
            }

            firstObject.last_collision.id = secondObject.id;
            firstObject.last_collision.key = secondObject.key;

            secondObject.touched = true;
        });

        //Allow the arrows to overlap the circle, instead of colliding to it and stopping
        this.physics.add.overlap(this.circle, this.arrows);

        //Add the player
        this.player = new Player(this, this.centerX, this.centerY);

        //Add the green overlay
        this.overlay = this.add.sprite(0, 0, 'overlay');
        this.overlay.setBlendMode(Phaser.BlendModes.SCREEN);
        this.overlay.setScale(this.bg_scale);
        this.overlay.setPosition(this.centerX, this.centerY);

        // Track the left/right arrow keys & AD
        const {LEFT, RIGHT, A, D} = Phaser.Input.Keyboard.KeyCodes;
        this.keys = this.input.keyboard.addKeys({
            left: LEFT,
            right: RIGHT,
            a: A,
            d: D
        });

        this.audio = {
            intro: this.sound.add('intro'),
            song: this.sound.add('master'),
            rewind: this.sound.add('rewind'),
            game_over: this.sound.add('game-over'),
            smash: this.sound.add('smash'),
            hit: this.sound.add('hit'),
        };

        _.each(this.audio, (audioEntry, audioKey) => {
            if (!audioEntry.audioBuffer) {
                this.audio[audioKey] = null;
            }
        })

        if (this.audio.hit) {
            this.audio.hit.setVolume(5.5);
        }
        if (this.audio.smash) {
            this.audio.smash.setVolume(5.5);
        }
        if (this.audio.rewind) {
            this.audio.rewind.setVolume(0.7);
        }

        //In record mode, we create a global variable, which will store the keys,
        //so we could quickly grab it in browser console
        if (this.game.record_mode) {
            console.warn('Record mode enabled');

            if (typeof window.recordedArrows === 'undefined') {
                window.recordedArrows = [];
            }

            if (typeof window.recordedAnimations === 'undefined') {
                window.recordedAnimations = [];
            }

            this.input.keyboard.on('keydown', (event) => {
                let time = parseFloat(parseFloat((Date.now() - this.start_time) / 1000).toFixed(1)) - (300 / this.speed);

                if (time < 0) {
                    return false;
                }

                if (this.game.record_mode === 'animations') {
                    window.recordedAnimations.push({
                        time: time.toFixed(1)
                    });
                } else {
                    console.log(event.code === 'ArrowLeft' ? 'left' : 'right' + ' - ' + time);

                    window.recordedArrows.push({
                        time: time.toFixed(1),
                        key: event.code === 'ArrowLeft' ? 'left' : 'right'
                    });
                }
            });
        }

        //Create the start overlay, with the start button and the countdown timer
        this.start_overlay = new StartOverlay(this);

        //Create the bell group, which will hold the 4 bells showing in the right top corner
        this.bells = this.add.group();

        //Create 4 bell icons, which will represent the player lives
        for (let i = 0; i < 4; i++) {
            if (this.dpr > 1 || this.is_mobile) {
                let bell = this.bells.create(this.innerWidth - 200 + (i * 50), 50, 'bell');
                bell.setScale(bell.scaleX / this.dpr)
            } else {
                this.bells.create(this.innerWidth - this.percentToPixels('width', 25) + (i * 50), 100, 'bell');
            }
        }

        this.textures.remove('score');

        let score = this.add.graphics();
        score.lineStyle(2, 0xCDDA00, 1)
        score.strokeCircle(27, 27, 25);
        score.generateTexture('score', 54, 54);
        score.destroy();

        let scoreTimeX = this.is_mobile ? this.innerWidth - 50 : this.percentToPixels('width', 25) - 50;
        let scoreTimeY = this.is_mobile ? 110 : 100;

        this.score = this.add.sprite(scoreTimeX, scoreTimeY, 'score');
        this.score.text = this.make.text({
            x: scoreTimeX,
            y: scoreTimeY,
            text: "0",
            style: {
                font: '200 22px "Scandinavian", sans-serif',
                fill: '#ffffff',
                align: 'center'
            },
        });
        this.score.text.setOrigin(0.5, 0.5);

        this.game_time = this.make.text({
            x: scoreTimeX,
            y: scoreTimeY + 50,
            text: "0:00",
            style: {
                font: '200 20px "Scandinavian", sans-serif',
                fill: '#ffffff',
                align: 'center'
            },
        });
        this.game_time.setOrigin(0.5, 0.5);

        this.mobile_keys = {
            left: false,
            right: false
        };

        //Create arrow buttons for the mobile players;
        if (this.is_mobile) {
            //Left arrow
            this.left_key = this.add.sprite(70, this.innerHeight - 70, 'left-key');
            this.left_key.original_scale = this.left_key.scaleX / this.dpr;
            this.left_key.setScale(this.left_key.original_scale - (0.1 / this.dpr));
            this.left_key.setInteractive();

            this.left_key.on('pointerdown', () => {
                this.tactileFeedback();

                this.left_key.setScale(this.left_key.original_scale);

                this.mobile_keys.left = true;
            });

            this.left_key.on('pointerup', () => {
                this.left_key.setScale(this.left_key.original_scale - (0.1 / this.dpr));

                this.mobile_keys.left = false;
            });

            //Right arrow
            this.right_key = this.add.sprite(this.innerWidth - 70, this.innerHeight - 70, 'right-key');
            this.right_key.original_scale = this.right_key.scaleX / this.dpr;
            this.right_key.setScale(this.right_key.original_scale - (0.1 / this.dpr));
            this.right_key.setInteractive();

            this.right_key.on('pointerdown', () => {
                this.tactileFeedback();

                this.right_key.setScale(this.right_key.original_scale);

                this.mobile_keys.right = true;
            });

            this.right_key.on('pointerup', () => {
                this.right_key.setScale(this.right_key.original_scale - (0.1 / this.dpr));

                this.mobile_keys.right = false;
            });
        }

        //Show the FPS at the top left corner, if debug mode is enabled
        if (this.game.debug_mode) {
            this.fps_text = this.make.text({
                x: 10,
                y: 10,
                text: "0",
                style: {
                    font: '200 18px "Scandinavian", sans-serif',
                    fill: '#ff00e8',
                },
            });
        }

        this.last_check = "0.0";

        //Once the song ends, play the game-over sound, or if the record mode is enabled create a popup with the
        //created arrow data
        if (this.audio.song) {
            this.audio.song.on('ended', () => {
                if (this.game.record_mode) {
                    localStorage.setItem('recordedArrows', JSON.stringify(window.recordedArrows))

                    this.sys.game.destroy(true);

                    this.game._vm.$nextTick(() => {
                        this.game._vm.$router.push({
                            name: 'recorded-arrows',
                            params: {
                                data: JSON.stringify(window.recordedArrows)
                            }
                        })
                    })
                } else {
                    if (this.audio.game_over) {
                        this.audio.game_over.play();
                    }
                }
            });
        }

        if (this.audio.game_over) {
            this.audio.game_over.on('ended', () => {
                //Once the game-over sound ends, finish the game
                this.finishGame();
            });
        }

        this.fallback_time = 65;
    }

    update(time, delta) {
        if (this.game.debug_mode) {
            this.fps_text.setText(parseFloat(this.game.loop.actualFps).toFixed(2));
        }

        if (this.start_overlay) {
            this.start_overlay.update();
        }

        if (!this.started || this.is_ending) {
            return false;
        }

        this.current_second = parseFloat((Date.now() - parseInt(this.start_time)) / 1000).toFixed(1);

        if (!this.game.record_mode) {
            //Find the arrow which needs to be created
            let availableArrow = this.available_arrows.find(availableArrow => {
                return availableArrow.time === this.current_second;
            });

            //Create an arrow, if one was found
            if (availableArrow) {
                let arrow = null;

                //Find an available arrow template
                this.arrows.children.each(child => {
                    if (!child.visible) {
                        arrow = child;
                    }
                });

                if (!arrow) {
                    console.warn('Couldn\'t find usable arrow sprite');
                }

                if (arrow) {
                    if (this.is_mobile) {
                        arrow.setPosition(this.circle.x, this.circle.y - 300)
                    } else {
                        arrow.setPosition(this.circle.x - 300, this.circle.y)
                    }

                    arrow.setTexture(availableArrow.key + '-icon');
                    arrow.setVisible(true);
                    arrow.setAlpha(0);

                    arrow.id = Phaser.Utils.String.UUID();
                    arrow.key = availableArrow.key;
                    arrow.touched = false;
                    arrow.marked = false;
                    arrow.fading = false;

                    //Redraw the body as a circle
                    arrow.body.setCircle(arrow.width / 2);

                    //Start moving the arrow
                    if (this.is_mobile) {
                        arrow.body.setVelocity(0, this.speed);
                    } else {
                        arrow.body.setVelocity(this.speed, 0);
                    }

                    //Fade in the sprite
                    this.tweens.add({
                        targets: arrow,
                        alpha: 1,
                        ease: 'Power1',
                        duration: 300,
                    });

                    //Reset the time on the available arrow, so a duplicate isn't made in the next update iteration
                    Phaser.Utils.Array.SpliceOne(this.available_arrows, 0);
                }
            }

            this.arrows.children.each(arrow => {
                if (arrow.touched && arrow.body.touching.none) {
                    //Change the texture of the arrow, when the arrow is out of the circle
                    let textureCheck;

                    if (this.is_mobile) {
                        textureCheck = arrow.y > (this.circle.y - (this.circle.width / 2)) && !arrow.marked
                    } else {
                        textureCheck = arrow.x > (this.circle.x - (this.circle.width / 2)) && !arrow.marked;
                    }

                    if (textureCheck) {
                        if (this.game.autoclick) {
                            this.handleSuccessfulHit(arrow);
                        } else {
                            arrow.setTexture('wrong-' + arrow.key + '-icon');
                        }
                    }

                    //Remove the arrow with fading, when it's far enough
                    let fadeCheck;

                    if (this.is_mobile) {
                        fadeCheck = arrow.y > this.circle.y + 150 && !arrow.fading;
                    } else {
                        fadeCheck = arrow.x > this.circle.x + 300 && !arrow.fading;
                    }

                    if (fadeCheck) {
                        arrow.fading = true;

                        this.tweens.add({
                            targets: arrow,
                            alpha: 0,
                            ease: 'Power1',
                            duration: 300,
                            onComplete: () => {
                                arrow.fading = false;
                                arrow.touched = false;
                                //Stop motion
                                arrow.setVelocity(0, 0);
                                //Move the arrow back to the top left corner
                                arrow.setPosition(0, 0);
                                //Make the arrow invisible, so it can be used in the next loop iteration
                                arrow.setVisible(false);
                            }
                        });
                    }

                    //If this the first time we've seen the arrow, remove a life.
                    if (!arrow.marked && !this.game.autoclick) {
                        this.removeLive();
                        this.showFailedHit();

                        if (this.audio.smash) {
                            this.audio.smash.play();
                        }
                    }

                    arrow.marked = true;
                }
            });

            this.last_check = this.current_second;
        }

        //Check if an arrow is currently overlapping with the circle
        if (this.circle && !this.circle.body.touching.none) {
            let arrow = this.getArrowById(this.circle.last_collision.id);

            if (arrow && !arrow.marked) {
                //Check if user is pressing the right key

                if ((this.keys.left.isDown || this.keys.a.isDown || this.mobile_keys.left) && arrow.key === 'left') {
                    this.handleSuccessfulHit(arrow);
                } else if ((this.keys.right.isDown || this.keys.d.isDown || this.mobile_keys.right) && arrow.key === 'right') {
                    this.handleSuccessfulHit(arrow);
                } else if ((this.keys.right.isDown || this.keys.d.isDown || this.mobile_keys.right) && arrow.key === 'left') {
                    this.handleMissedHit(arrow);
                } else if ((this.keys.left.isDown || this.keys.a.isDown || this.mobile_keys.left) && arrow.key === 'right') {
                    this.handleMissedHit(arrow);
                }
            }
        }

        //Update player animation
        this.player.update(this.current_second);

        //Finish the game, if the player doesn't have any more lives
        if (this.lives <= 0) {
            //Mark the game as ending, so the game loop wouldn't catch the key presses and wouldn't add new arrows
            this.is_ending = true;

            //Play the rewind sound
            if (this.audio.rewind) {
                this.audio.rewind.play();
            }

            //Stop the song
            if (this.audio.song) {
                this.audio.song.stop();
            }

            //Destroy arrows currently on screen
            this.arrows.children.each(arrow => {
                this.tweens.add({
                    targets: arrow,
                    alpha: 0,
                    ease: 'Power1',
                    duration: 300,
                    onComplete: () => {
                        arrow.destroy();
                    }
                });
            });

            //Stop the player animations
            this.player.stop();
        }

        let seconds = this.fallback_time - parseInt(this.current_second);
        if (this.audio.song && this.audio.song.isPlaying) {
            seconds = this.audio.song.totalDuration - parseInt(this.current_second);
        }

        this.game_time.setText(this.secondsToTime(seconds > 0 ? seconds : 0));

        //Find the speed marker, and increase the arrow speed
        let speedMarker = this.speed_markers.find(speedMarker => {
            return speedMarker.time === this.current_second;
        });
        if (speedMarker) {
            this.speed = speedMarker.speed;

            //Increase the speed for the arrows on the screen
            this.arrows.children.each((child) => {
                if (child.visible) {
                    if (this.is_mobile) {
                        child.body.setVelocity(0, this.speed);
                    } else {
                        child.body.setVelocity(this.speed, 0);
                    }
                }
            });
        }

        //On IE, the audio doesn't work, so we have to manually end the game, based on time
        if (this.game.is_ie && parseInt(this.current_second) >= this.fallback_time) {
            this.finishGame();
        }
    }

    /**
     * Returns an arrow by the ID
     *
     * @param id
     * @return {boolean}
     */
    getArrowById(id) {
        let sprite = false;

        this.arrows.children.each(arrow => {
            if (arrow.id === id) {
                sprite = arrow;
            }
        })

        return sprite;
    }

    addHit() {
        this.hits += 1;

        this.score.text.setText(this.hits);
    }

    /**
     * Shows a random good status and slightly increases and decreases the size of the center circle
     */
    showSuccessfulHit() {
        let possibility = _.random(2);

        if (possibility === 1 && !this.is_mobile) {
            let index = _.random(3);

            //Remove the previous status
            if (this.hitStatus) {
                this.hitStatus.destroy();
            }

            //Add the new status
            this.hitStatus = this.make.text({
                x: this.is_mobile ? this.centerX : this.circle.x,
                y: this.is_mobile ? this.innerHeight - 150 : this.circle.y - 65,
                text: this.good_statuses[index],
                style: {
                    font: '18px "Scandinavian", sans-serif',
                    fill: '#ffffff'
                }
            });

            //Centers the text block pointer
            this.hitStatus.setOrigin(0.5)
        }

        //Make the circle slightly larger, and then make it smaller again
        this.tweens.add({
            targets: this.circle,
            scaleX: this.circle.original_scale,
            scaleY: this.circle.original_scale,
            ease: 'Power1',
            duration: 300,
            yoyo: true
        });
    }

    /**
     * Shows the first available bad status above the center circle
     */
    showFailedHit() {
        if (this.is_mobile) {
            return false;
        }

        if (!this.bad_statuses.length) {
            return false;
        }

        //Remove the previous status
        if (this.hitStatus) {
            this.hitStatus.destroy();
        }

        //Add the new status
        this.hitStatus = this.make.text({
            x: this.is_mobile ? this.centerX : this.circle.x,
            y: this.is_mobile ? this.innerHeight - 150 : this.circle.y - 65,
            text: this.bad_statuses[0],
            style: {
                font: '18px "Scandinavian", sans-serif',
                fill: '#ffffff'
            }
        });

        //Centers the text block pointer
        this.hitStatus.setOrigin(0.5)

        //Remove the used status message
        this.bad_statuses.splice(0, 1);
    }

    /**
     * Increases score, randomly shows good status message, changes the arrow texture
     *
     * @param arrow
     */
    handleSuccessfulHit(arrow) {
        this.addHit();

        this.showSuccessfulHit();

        arrow.setTexture('correct-' + arrow.key + '-icon');

        arrow.marked = true;

        if (this.audio.hit) {
            this.audio.hit.play();
        }
    }

    /**
     * Triggers live removal, changes the arrow texture un plays the smash sound
     */
    handleMissedHit(arrow) {
        this.removeLive();

        this.showFailedHit();

        arrow.setTexture('wrong-' + arrow.key + '-icon');

        arrow.marked = true;

        if (this.audio.smash) {
            this.audio.smash.play();
        }
    }

    /**
     * Removes a live, and handles bell texture changing
     */
    removeLive() {
        if (this.game.god_mode) {
            return false;
        }

        this.lives--;

        let removed = false;

        this.bells.children.each(child => {
            if (!removed && child.texture.key === 'bell') {
                child.setTexture('bell-grey');
                removed = true;
            }
        });
    }

    /**
     * Starts the main game process
     */
    startGame() {
        this.started = true;

        //Set the game start time
        this.start_time = Date.now();

        //Start the player animation
        this.player.start();

        //Play the intro song
        if (this.audio.intro) {
            this.audio.intro.play();
        }

        this.time.delayedCall(3000, () => {
            //Start the background animation
            this.bg.play('bg')

            //Stop the intro song
            if (this.audio.intro) {
                this.audio.intro.stop();
            }

            //Start the real song
            if (this.audio.song) {
                this.audio.song.play();
            }

            //Move the circle up front, so it wouldn't be blocked by the player animation
            this.circle.setDepth(5);
        }, [], this);
    }

    /**
     * Destroys the current game and redirects the player to the game-over page
     */
    finishGame() {
        if ((this.game.endless_mode && this.game.record_mode) || (this.game.endless_mode || this.game.record_mode)) {
            return false;
        }

        this.sys.game.destroy(true);

        this.game._vm.$nextTick(() => {
            this.game._vm.$router.push({
                name: 'game-score',
                params: {
                    hits: this.hits,
                    hits_needed: this.hits_needed
                }
            })
        })
    }

    /**
     * Calculates and returns pixel count based on given percent
     * @param base
     * @param percent
     * @return {number}
     */
    percentToPixels(base, percent) {
        if (base === 'width') {
            return this.innerWidth * (percent / 100)
        }

        return this.innerHeight * (percent / 100)
    }

    /**
     * Will slightly vibrate the device if the browser supports the Vibrate API
     */
    tactileFeedback(ms) {
        if (typeof navigator.vibrate !== 'undefined') {
            navigator.vibrate(typeof ms !== 'undefined' ? ms : 50);
        }
    }

    secondsToTime(seconds) {
        const minutes = (seconds % 3600) / 60
        seconds %= 60

        return [minutes, seconds].map(this.format).join(':')
    }

    format(val) {
        return ('0' + Math.floor(val)).slice(-2)
    }
}